import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, apiClient, supportPath } from '@/lib/api-client';
import type {
  BugBountyCampaign,
  BugBountyGrantResult,
  BugReportFilter,
  BugReportPage,
} from '@/types/bug-reports';

/** Centralized query keys for the bug-bounty operator surface. */
export const bugReportKeys = {
  all: ['bug-reports'] as const,
  page: (status: BugReportFilter, offset: number) =>
    ['bug-reports', 'page', status, offset] as const,
  campaign: () => ['bug-reports', 'campaign'] as const,
};

/** One page of the queue. 50 rows — there are 638 open reports, so this always pages. */
export const BUG_REPORT_PAGE_SIZE = 50;

/** GET /admin/support/bug-reports — a page of reports plus the per-status counts. */
export function useBugReports(
  status: BugReportFilter,
  offset: number
): UseQueryResult<BugReportPage, ApiError> {
  return useQuery({
    queryKey: bugReportKeys.page(status, offset),
    queryFn: ({ signal }) =>
      apiClient.get<BugReportPage>(
        supportPath('/bug-reports', { status, limit: BUG_REPORT_PAGE_SIZE, offset }),
        signal
      ),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/** GET /admin/support/bug-bounty/campaign — the flag, the cap and whether a confirm can pay. */
export function useBugBountyCampaign(): UseQueryResult<BugBountyCampaign, ApiError> {
  return useQuery({
    queryKey: bugReportKeys.campaign(),
    queryFn: ({ signal }) =>
      apiClient.get<BugBountyCampaign>(supportPath('/bug-bounty/campaign'), signal),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * PUT /admin/support/bug-bounty/campaign — flip the flag, set the cap (null = uncapped).
 *
 * Audited server-side to `activity_log` with the operator's JWT subject. This is the switch
 * that starts the payouts, so the dialog in the page asks before it is sent.
 */
export function useUpdateBugBountyCampaign(): UseMutationResult<
  BugBountyCampaign,
  ApiError,
  { isActive: boolean; maxGrants: number | null }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (next) =>
      apiClient.put<BugBountyCampaign>(supportPath('/bug-bounty/campaign'), next),
    onSuccess: (campaign) => {
      queryClient.setQueryData(bugReportKeys.campaign(), campaign);
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.all });
    },
  });
}

/**
 * POST /admin/support/bug-reports/:reference/confirm — grant the reporter Premium.
 *
 * Invalidates the whole key space rather than patching the row: a grant moves the ticket
 * between status buckets AND bumps the campaign's counter, so every count on screen is stale
 * the moment this succeeds.
 */
export function useConfirmBugReport(): UseMutationResult<BugBountyGrantResult, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reference) =>
      apiClient.post<BugBountyGrantResult>(
        supportPath(`/bug-reports/${encodeURIComponent(reference)}/confirm`),
        {}
      ),
    onSuccess: (_result, reference) => {
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.all });
      // Without this the detail page keeps showing pre-grant status/buttons after a
      // successful grant — useUpdateTicketStatus already invalidates both keys; this one
      // only had the list's.
      void queryClient.invalidateQueries({ queryKey: ['bug-reports', 'detail', reference] });
    },
  });
}
